import { formatUnits } from '@ethersproject/units';
import React, { memo, useCallback, useContext } from 'react';
import { SpoolDto } from '../../../../types/spool-local';
import {
    CLAIM_REWARDS_PENDING,
    CLAIM_WITHDRAWAL,
    REWARDS_CLAIMED,
    WITHDRAWAL_COMPLETED,
} from '../../../../config/constants';
import { useWeb3React } from '@web3-react/core';
import modalContext from '../../../../context/modal/modalContext';
import errorContext from '../../../../context/error/errorContext';
import appContext from '../../../../context/app/appContext';
import useUserBalance from '../../../../hooks/web3/useUserBalance';
import { sleep } from '../../../../web3/utils';
import { formatUSD } from '../../../../helpers/formats';
import Button from '../../../Button';
import ExternalLink from '../../../ExternalLink';
import StepCounter from '../../../StepCounter';
import IncentivizedRewards from './IncentivizedRewards';
import WithdrawalStatus from './WithdrawalStatus';
import useUserSpools from '../../../../hooks/web3/vault/useUserSpools';

type Props = {
    spoolDetails: SpoolDto;
    rowCollapsed: boolean;
};

const UserDetails: React.FC<Props> = ({ rowCollapsed, spoolDetails }) => {
    const { account } = useWeb3React();
    const { openModal, closeModal, setModalData } = useContext(modalContext);
    const { setError } = useContext(errorContext);
    const { sdk, interfaceSDK } = useContext(appContext);

    const { updateUserSpool } = useUserSpools();
    const { asset, tvr, incentivizedRewards, userData } = spoolDetails;

    const userBalance = useUserBalance(asset?.id);

    const claimRewards = useCallback(async () => {
        try {
            if (!account) {
                openModal('connectWalletModal');
                return;
            }

            if (!sdk) return;

            setModalData((prev) => ({ ...prev, message: CLAIM_REWARDS_PENDING }));
            openModal('waitingModal');

            const claim = await sdk.spools.claimIncentivizedRewards(spoolDetails.address);

            const receipt = await claim.wait();

            await sleep(5000);

            await updateUserSpool(spoolDetails, 'userIncentivizedRewards');

            setModalData((prev) => ({
                ...prev,
                status: REWARDS_CLAIMED,
                message: '',
                txHash: receipt.transactionHash,
            }));

            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    }, [
        sdk,
        account,
        spoolDetails,
        openModal,
        closeModal,
        setModalData,
        setError,
        updateUserSpool,
    ]);

    const claimWithdraw = useCallback(async () => {
        try {
            if (!interfaceSDK) return;

            setModalData((prev) => ({ ...prev, message: CLAIM_WITHDRAWAL }));
            openModal('waitingModal');

            const contract = await interfaceSDK.get.getVaultContractInstance(
                spoolDetails.address
            );

            const orderedStrategies = spoolDetails.strategies.map(
                ({ address }) => address
            );

            const claim = await contract.claim(true, orderedStrategies, true);

            const receipt = await claim.wait();

            await sleep(5000);

            await updateUserSpool(spoolDetails, 'userClaimableAmount');

            setModalData((prev) => ({
                ...prev,
                status: WITHDRAWAL_COMPLETED,
                message: '',
                txHash: receipt.transactionHash,
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    }, [
        interfaceSDK,
        spoolDetails,
        openModal,
        closeModal,
        setModalData,
        setError,
        updateUserSpool,
    ]);

    return (
        <div className='user-details'>
            <span className='note'>Data is recent but may not be up to date</span>
            <WithdrawalStatus
                userSpool={userData}
                asset={asset}
                claimWithdraw={claimWithdraw}
            />
            <div className='user-details-inner__right'>
                <div className='deposit'>
                    <div className='balance'>
                        <span>
                            Your Wallet's Current {asset?.symbol ?? 'XXX'} Balance :{' '}
                        </span>
                        <span className='amount'>
                            {formatUSD(
                                userBalance
                                    ? formatUnits(userBalance, asset?.decimals)
                                    : 0
                            )}
                        </span>
                    </div>
                    <Button
                        onClick={() =>
                            openModal(account ? 'spoolDeposit' : 'connectWalletModal')
                        }
                    >
                        Deposit Now
                    </Button>
                    <div className='accepted-token'>
                        <p>
                            This Spool can only accept {asset?.symbol ?? 'XXX'}. To
                            convert other tokens to {asset?.symbol ?? 'XXX'} you can use{' '}
                            <ExternalLink to='https://curve.fi/'>Curve</ExternalLink> or
                            any other DEX of your choosing
                        </p>
                    </div>
                </div>
                <div className='earned-yield'>
                    <span>Your Yield Earned to Date ({asset?.symbol ?? 'XXX'})</span>
                    <StepCounter
                        className='amount'
                        end={userData?.userYieldEarnedToDateAmount || 0}
                        duration={3}
                        isCounting={rowCollapsed}
                    />
                </div>
                <IncentivizedRewards
                    incentivizedRewards={incentivizedRewards}
                    userSpool={userData}
                    claimRewards={claimRewards}
                />
                <div className='current-tvr'>
                    <span>This Spools' Current TVR</span>
                    <span className='tvr'>
                        {tvr === '0'
                            ? 'N/A'
                            : `~${formatUSD(tvr || 0)} ${asset?.symbol ?? 'XXX'}`}
                    </span>
                </div>
            </div>
            {/* <TvrChart /> */}
        </div>
    );
};

export default memo(UserDetails);
